<template>
  <section
    class="shared-component"
    :class="`shared-component--${theme}`"
  >
    <div
      class="ds-container"
      :class="[
        {
          'ds-padding-bottom-48 ds-md-padding-top-72': withSpaceTop,
          'ds-padding-bottom-48 ds-md-padding-bottom-96': withSpaceBottom
        }
      ]"
    >
      <DsMediaLayout
        class="ds-media-text-component"
        :orientation="computedOrientation"
        :media-columns-width="{ small: 12, medium: 6, large: 6 }"
        :content-columns-width="{ small: 12, medium: 6, large: 5 }"
        :full-width-content="fullWidthContent"
        :full-width-media="fullWidthContent"
        :y-align="yAlign"
      >
        <template #media>
          <template v-if="video">
            <DsVideoElement
              v-if="videoAutoplay"
              v-bind="video"
              :screen="screen"
              :aria-label-play="labels.play"
              :aria-label-close="labels.close"
              :class="{
                'ds-margin-top-48': computedOrientation === 'bottom',
                'ds-margin-bottom-48': computedOrientation === 'top'
              }"
              data-j-video-autoplay
            />
            <DsVideoPlayer
              v-else
              v-bind="video"
              :screen="screen"
              :class="{
                'ds-margin-top-48': computedOrientation === 'bottom',
                'ds-margin-bottom-48': computedOrientation === 'top'
              }"
              data-j-video
            />
          </template>
          <DsImageElement
            v-else-if="image"
            :image="image"
            :screen="screen"
            :class="{
              'ds-margin-top-48': computedOrientation === 'bottom',
              'ds-margin-bottom-48': computedOrientation === 'top'
            }"
            data-j-image
          />
        </template>
        <template #content>
          <div
            class="shared-component__container ds-display-flex ds-flex-direction-column ds-justify-content-space-between"
            :class="{ 'ds-justify-content-flex-end': orientation === 'bottom' }"
          >
            <div
              v-if="logo || linkBack"
              class="ds-padding-bottom-24"
            >
              <img
                v-if="logo"
                class="shared-component__logo"
                :class="{ 'shared-component--small': smallLogo }"
                :src="logo.src"
                :alt="logo.name"
                data-j-logo
              />
              <DsLinkBack
                v-if="linkBack"
                :tracking-data="{ 'component-name': $options.name, text: linkBack.text, title }"
                :text="linkBack.text"
                :url="linkBack.url"
              />
            </div>
            <div class="shared-component__content">
              <DsEyebrow
                :text="eyebrow"
                :icon="icon"
              />
              <div
                class="shared-component__wrapper"
                :class="{ 'shared-component__wrapper--no-media': !hasMediaElement }"
              >
                <h1
                  class="ds-heading-large"
                  :class="{
                    'ds-lg-heading-xlarge': orientation === 'left' || orientation === 'right',
                    'ds-md-heading-large': orientation === 'top' || orientation === 'bottom'
                  }"
                >
                  {{ title }}
                </h1>
                <slot name="description">
                  <div
                    v-if="description"
                    class="ds-text-medium sm-only-padding"
                  >
                    {{ description }}
                  </div>
                </slot>
                <div
                  v-if="links && links.length > 1"
                  class="ds-buttons-wrapper"
                >
                  <DsLink
                    v-if="primaryLink"
                    :open-in-new-window="primaryLink.openInNewWindow"
                    :nuxt="primaryLink.nuxt"
                    :url="primaryLink.url"
                    :content="primaryLink.title"
                    :tracking-data="{ 'component-name': $options.name, text: primaryLink.title, title: title }"
                    type="button"
                    color="green"
                  />
                  <DsLink
                    v-if="secondaryLink"
                    :url="secondaryLink.url"
                    :open-in-new-window="secondaryLink.openInNewWindow"
                    :nuxt="secondaryLink.nuxt"
                    :content="secondaryLink.title"
                    :icon-right="{ category: 'arrows', name: 'chevron-right-small' }"
                    :tracking-data="{ 'component-name': $options.name, text: secondaryLink.title, title: title }"
                    variant="secondary"
                    color="green"
                    class="ds-md-margin-left-24"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </DsMediaLayout>
    </div>
  </section>
</template>

<script setup>
import {
  DsImageElement,
  DsLink,
  DsLinkBack,
  DsMediaLayout,
  DsVideoElement,
  DsVideoPlayer,
  validatorOrientation,
  DsEyebrow
} from 'awds'

const { screen } = useScreen()
const { labels } = useGlobalData()

const props = defineProps({
  ...themeProp,
  orientation: {
    type: String,
    default: 'right',
    validator: (str) => validatorOrientation(str)
  },
  image: {
    type: Object,
    default: null
  },
  video: {
    type: Object,
    default: null
  },
  videoAutoplay: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  links: {
    type: Array,
    default: null
  },
  eyebrow: {
    type: String,
    default: ''
  },
  linkBack: {
    type: Object,
    default: null,
    validator: (obj) => ['text', 'url'].every((key) => key in obj)
  },
  logo: {
    type: Object,
    default: null
  },
  withBoldTag: {
    type: Boolean,
    default: false
  },
  withTitleSizeLarge: {
    type: Boolean,
    default: false
  },
  withSpaceBottom: {
    type: Boolean,
    default: true
  },
  withSpaceTop: {
    type: Boolean,
    default: true
  },
  smallLogo: {
    type: Boolean,
    default: false
  },
  yAlign: {
    type: String,
    // All Media text blocks vertically center aligns the content.
    default: 'none',
    validator: (str) => ['center', 'stretch', 'flex-end', 'none'].includes(str)
  },
  icon: {
    type: Object,
    default: null
  },
  theme: {
    type: String,
    default: 'light'
  }
})

const computedOrientation = computed(() => {
  if (screen.value === 'small') {
    return props.orientation === 'bottom' ? 'bottom' : 'top'
  }
  return props.orientation
})

const fullWidthContent = computed(() => {
  if (!props.image && !props.video) {
    return true
  }
  return props.orientation === 'top' || props.orientation === 'bottom'
})

const contentColumnsWidth = computed(() => {
  return { small: 12, medium: 6, large: 6 }
})

const hasMediaElement = computed(() => {
  return !!(props.image || props.video)
})

const primaryLink = computed(() => {
  if (!props.links?.length > 0) {
    return null
  }
  return props.links[0]
})

const secondaryLink = computed(() => {
  if (!props.links?.length > 1) {
    return null
  }
  return props.links[1]
})
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.shared-component {
  min-height: $spacing-192;

  &__icon {
    max-width: $spacing-24;
  }

  &--light {
    background-color: $color-white;
  }

  &--grey {
    background-color: $color-grey-100;
  }

  &--dark {
    background-color: $color-black;
    color: $color-white;
  }

  &--green {
    background-color: $color-green;
  }

  &__wrapper {
    display: grid;
    gap: 24px;
    grid-template-columns: 1fr;

    h2 {
      text-wrap: balance;
    }
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
  }

  &__logo {
    height: 70px;
    width: auto;

    &--small {
      height: 28px;
    }
  }
}
</style>
